import type { ChangeEvent, Dispatch, DragEvent, SetStateAction } from 'react';
import { FC, Fragment } from 'react';
import editProfileInfo from '../../assets/editAdminIcon.svg';
import FileIcon from '../../assets/Path.svg';
import ArrowDownDown from '../../components/Icons/ArrowDownDown';
import { Button, Form, UploadFile } from 'antd';
import { FloatInput } from '../../components/Fields/FloatInput';
import { FilePicker } from '../../components/Fields/FilePicker';
import { FileSkipped } from '../../components/Icons/FileSkipped';
import { CalendarSvg } from '../../components/Icons/CalendarIcon';

export interface TimeSlot {
    id: number;
    date: string;
    time_from: string;
    time_to: string;
}

/** Стабильный ключ слота: для React key и перестановок без гонок по индексу */
export const getSlotKey = (slot: TimeSlot) => `${slot.date}|${slot.time_from}|${slot.time_to}|${slot.id}`;

export interface ParticipantI {
    title: string;
    full_name: string;
}

const statusCheckIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle
            cx="9.99922"
            cy="10"
            r="7.50312"
            stroke="#001965"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M7.03516 10.2826L8.84174 12.0892L8.83007 12.0775L12.9059 8.00167"
            stroke="#001965"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const DragIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="9" cy="5" r="1" fill="currentColor" />
        <circle cx="15" cy="5" r="1" fill="currentColor" />
        <circle cx="9" cy="12" r="1" fill="currentColor" />
        <circle cx="15" cy="12" r="1" fill="currentColor" />
        <circle cx="9" cy="19" r="1" fill="currentColor" />
        <circle cx="15" cy="19" r="1" fill="currentColor" />
    </svg>
);

const CalendarIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
);

const ClockIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
    </svg>
);

const steps = [{ key: 'slots' }, { key: 'participants' }, { key: 'file' }];

export interface SupplierPitchFormStepsProps {
    activeStep: number;
    setActiveStep: Dispatch<SetStateAction<number>>;
    newSlot: string;
    setNewSlot: Dispatch<SetStateAction<string>>;
    slots: TimeSlot[] | undefined;
    moveSlot: (fromSlotKey: string, toSlotKey: string, opts?: { pulse?: boolean }) => void;
    handleDragStart: (slotKey: string) => void;
    handleDragOver: (e: DragEvent, slotKey: string) => void;
    handleDragEnd: () => void;
    draggedSlotKey: string | null;
    /** Slot row currently highlighted as drop target while dragging. */
    dragOverSlotKey: string | null;
    /** Row that recently moved via arrow buttons — plays reorder feedback animation. */
    slotReorderPulseKey: string | null;
    setRequestNewSlotModal: Dispatch<SetStateAction<boolean>>;
    setWithDrawModal: Dispatch<SetStateAction<boolean>>;
    representativesCount: number | undefined;
    participantsData: ParticipantI[];
    handleInputChange: (index: number, value: string, field: keyof ParticipantI) => void;
    errors: number[];
    handleSave: () => void;
    file: UploadFile[];
    setFile: Dispatch<SetStateAction<UploadFile[]>>;
    fileError: boolean;
    setFileError: Dispatch<SetStateAction<boolean>>;
}

export const SupplierPitchFormSteps: FC<SupplierPitchFormStepsProps> = ({
    activeStep,
    setActiveStep,
    newSlot,
    setNewSlot,
    slots,
    moveSlot,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    draggedSlotKey,
    dragOverSlotKey,
    slotReorderPulseKey,
    setRequestNewSlotModal,
    setWithDrawModal,
    representativesCount,
    participantsData,
    handleInputChange,
    errors,
    handleSave,
    file,
    setFile,
    fileError,
    setFileError
}) => (
    <>
        {steps.map((elem) => {
            if (elem.key === 'slots') {
                if (newSlot) {
                    return (
                        <section key="slots" className="supplier-pitch__section">
                            <div className="supplier-pitch__section-header">
                                <span className="supplier-pitch__section-number inactive">1</span>
                                <h2 className="supplier-pitch__section-title">Select Your Preferred Pitch Slots</h2>
                            </div>
                            <div className="supplier-pitch__newSlot">
                                <div className="supplier-pitch__newSlot-title">
                                    {' '}
                                    <div className="slot-value orange">
                                        <CalendarSvg fill="#E64A0D" className={''} />
                                        New Slot Requested
                                    </div>
                                    <Button
                                        onClick={() => {
                                            setNewSlot('');
                                            setActiveStep(1);
                                        }}
                                    >
                                        Cancel Request
                                    </Button>
                                </div>
                                <div className="supplier-pitch__reason">
                                    <h1>Comment</h1>
                                    <div>{newSlot}</div>
                                </div>
                            </div>
                        </section>
                    );
                }
                if (activeStep === 1) {
                    return (
                        <section key="slots" className="supplier-pitch__section">
                            <div className="supplier-pitch__section-header">
                                <span className="supplier-pitch__section-number">1</span>
                                <h2 className="supplier-pitch__section-title">Select Your Preferred Pitch Slots</h2>
                            </div>

                            <p className="supplier-pitch__description">
                                Rank the available time slots from most preferred (1) to least preferred.
                            </p>

                            <ul className="supplier-pitch__instructions">
                                <li>Priority 1 is your most preferred slot.</li>
                                <li>If no preferences are submitted, a slot will be assigned automatically.</li>
                            </ul>

                            <div className="supplier-pitch__slots">
                                <div className="supplier-pitch__slots-header">
                                    <span></span>
                                    <span>Priority</span>
                                    <span>Date & Time</span>
                                </div>

                                <div className={`slots-list${draggedSlotKey ? ' slots-list--dragging' : ''}`}>
                                    {slots?.map((slot: TimeSlot, index: number) => {
                                        const slotKey = getSlotKey(slot);
                                        const isDragging = draggedSlotKey === slotKey;
                                        const isDragOver =
                                            Boolean(draggedSlotKey) &&
                                            dragOverSlotKey === slotKey &&
                                            draggedSlotKey !== slotKey;
                                        const isReorderPulse = slotReorderPulseKey === slotKey;
                                        return (
                                            <div
                                                key={slotKey}
                                                className={`slot-item-pitch${isDragging ? ' dragging' : ''}${
                                                    isDragOver ? ' drag-over' : ''
                                                }${isReorderPulse ? ' reorder-pulse' : ''}`}
                                                draggable
                                                onDragStart={(e) => {
                                                    e.dataTransfer.effectAllowed = 'move';
                                                    e.dataTransfer.setData('text/plain', slotKey);
                                                    handleDragStart(slotKey);
                                                }}
                                                onDragOver={(e) => handleDragOver(e, slotKey)}
                                                onDragEnd={handleDragEnd}
                                            >
                                                <div className="slot-controls">
                                                    <button className="drag-handle" aria-label="Drag to reorder">
                                                        <DragIcon />
                                                    </button>

                                                    <div className="arrow-buttons">
                                                        {index === 0 ? (
                                                            <div className="arrow-btn"></div>
                                                        ) : (
                                                            <button
                                                                className="arrow-btn"
                                                                onClick={() => {
                                                                    const prev = slots?.[index - 1];
                                                                    if (prev) moveSlot(slotKey, getSlotKey(prev), { pulse: true });
                                                                }}
                                                                aria-label="Move up"
                                                            >
                                                                <ArrowDownDown
                                                                    style={{
                                                                        transform: 'rotate(180deg)'
                                                                    }}
                                                                />
                                                            </button>
                                                        )}
                                                        {index === (slots?.length ?? 0) - 1 ? (
                                                            <div className="arrow-btn"></div>
                                                        ) : (
                                                            <button
                                                                className="arrow-btn"
                                                                onClick={() => {
                                                                    const nextSlot = slots?.[index + 1];
                                                                    if (nextSlot)
                                                                        moveSlot(slotKey, getSlotKey(nextSlot), {
                                                                            pulse: true
                                                                        });
                                                                }}
                                                                aria-label="Move down"
                                                            >
                                                                <ArrowDownDown />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="slot-info">
                                                    <div className="slot-priority">№ {index + 1}</div>

                                                    <div className="slot-details">
                                                        <span className="slot-date">
                                                            <CalendarIcon />
                                                            {slot.date}
                                                        </span>
                                                        <span className="slot-time">
                                                            <ClockIcon />
                                                            {slot.time_from} - {slot.time_to}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="supplier-pitch__btns">
                                <Button
                                    type="default"
                                    className="supplier-pitch__btn"
                                    onClick={() => setRequestNewSlotModal(true)}
                                >
                                    Request New Slots
                                </Button>
                                <Button className="supplier-pitch__btn quit" onClick={() => setWithDrawModal(true)}>
                                    Withdraw from Process
                                </Button>
                                <Button className="supplier-pitch__btn blue-btn" onClick={() => setActiveStep(2)}>
                                    Submit Preferred Slots
                                </Button>
                            </div>
                        </section>
                    );
                }
                return (
                    <section key="slots" className="supplier-pitch__section">
                        <div className="supplier-pitch__section-header">
                            <span className="supplier-pitch__section-number inactive">1</span>
                            <h2 className="supplier-pitch__section-title">Select Your Preferred Pitch Slots</h2>
                        </div>
                        <div className="supplier-pitch__status-bar">
                            <div className="supplier-pitch__status-info">
                                <span className="supplier-pitch__status-icon"> {statusCheckIcon}</span>
                                <span className="supplier-pitch__status-text">Preferred pitch slots prioritized</span>
                            </div>
                            <button type="button" className="supplier-pitch__status-action">
                                <img src={editProfileInfo} alt="" onClick={() => setActiveStep(1)} />
                            </button>
                        </div>
                    </section>
                );
            }
            if (elem.key === 'participants') {
                if (activeStep === 2) {
                    return (
                        <section key="participants" className="supplier-pitch__section">
                            <div className="supplier-pitch__section-header">
                                <span className="supplier-pitch__section-number">2</span>
                                <h2 className="supplier-pitch__section-title">Participant Information</h2>
                            </div>

                            <p className="supplier-pitch__description">Provide information about participants</p>
                            {Array(representativesCount)
                                ?.fill(1)
                                ?.map((_elem: unknown, index: number) => (
                                    <Fragment key={index}>
                                        <div
                                            className={`slot ${errors.includes(index) ? 'error' : ''}`}
                                            id="participants"
                                        >
                                            <div className="slot__title">Participant {index + 1}</div>
                                            <Form className="slot__form">
                                                <Form.Item className="input-wrapper">
                                                    <FloatInput
                                                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                            handleInputChange(index, e.target.value, 'title')
                                                        }
                                                        floatLabel={'Title'}
                                                        required
                                                        className="input"
                                                        maxLength={60}
                                                        value={participantsData[index]?.title}
                                                    />
                                                </Form.Item>

                                                <Form.Item className="input-wrapper">
                                                    <FloatInput
                                                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                            handleInputChange(index, e.target.value, 'full_name')
                                                        }
                                                        floatLabel={'Full Name'}
                                                        required
                                                        className="input"
                                                        maxLength={128}
                                                        value={participantsData[index]?.full_name}
                                                    />
                                                </Form.Item>
                                            </Form>
                                        </div>
                                        {errors.includes(index) && (
                                            <div className="error-text" style={{ margin: '-15px 0 10px 0' }}>
                                                {index === 0
                                                    ? 'These are required fields'
                                                    : 'Please, specify Title and Full Name'}
                                            </div>
                                        )}
                                    </Fragment>
                                ))}
                            <div className="supplier-pitch__btns">
                                <Button className="supplier-pitch__btn save blue-btn" onClick={handleSave}>
                                    Next
                                </Button>
                            </div>
                        </section>
                    );
                }
                if (activeStep === 1) {
                    return (
                        <section key="participants" className="supplier-pitch__section">
                            <div className="supplier-pitch__section-header">
                                <span className="supplier-pitch__section-number inactive">2</span>
                                <h2 className="supplier-pitch__section-title">Participant Information</h2>
                            </div>
                        </section>
                    );
                }
                return (
                    <section key="participants" className="supplier-pitch__section">
                        <div className="supplier-pitch__section-header">
                            <span className="supplier-pitch__section-number inactive">2</span>
                            <h2 className="supplier-pitch__section-title">Participant Information</h2>
                        </div>
                        <div className="supplier-pitch__status-bar">
                            <div className="supplier-pitch__status-info">
                                <span className="supplier-pitch__status-icon">{statusCheckIcon}</span>
                                <span className="supplier-pitch__status-text">Participant info provided</span>
                            </div>
                            <button type="button" className="supplier-pitch__status-action">
                                <img src={editProfileInfo} alt="" onClick={() => setActiveStep(2)} />
                            </button>
                        </div>
                    </section>
                );
            }
            if (elem.key === 'file') {
                if (activeStep === 3) {
                    return (
                        <section key="file" className="supplier-pitch__section">
                            <div className="supplier-pitch__section-header">
                                <span className="supplier-pitch__section-number">3</span>
                                <h2 className="supplier-pitch__section-title">Upload Pitch Presentation</h2>
                            </div>

                            <p className="supplier-pitch__description">Upload a file to import Pitch Presentation</p>
                            <div className="supplier-pitch__file">
                                <Form.Item name="file">
                                    <FilePicker
                                        disabled={false}
                                        fileList={file}
                                        onChange={(next: UploadFile[]) => {
                                            setFile(next);
                                            setFileError(false);
                                        }}
                                        error={fileError ? 'This field is required' : ''}
                                        setError={() => {}}
                                        specialFormats={['pdf']}
                                    />
                                </Form.Item>
                            </div>
                            <div className="supplier-pitch__btns">
                                <Button className="supplier-pitch__btn" onClick={() => setActiveStep(4)}>
                                    Skip Uploading
                                </Button>
                                <Button
                                    className="supplier-pitch__btn save"
                                    onClick={() => {
                                        if (!file?.length) {
                                            setFileError(true);
                                        } else {
                                            setActiveStep(5);
                                        }
                                    }}
                                >
                                    Next
                                </Button>
                            </div>
                        </section>
                    );
                }
                if (activeStep === 1 || activeStep === 2) {
                    return (
                        <section key="file" className="supplier-pitch__section">
                            <div className="supplier-pitch__section-header">
                                <span className="supplier-pitch__section-number inactive">3</span>
                                <h2 className="supplier-pitch__section-title">Upload Pitch Presentation</h2>
                            </div>
                        </section>
                    );
                }
                if (activeStep === 4) {
                    return (
                        <section key="file" className="supplier-pitch__section">
                            <div className="supplier-pitch__section-header">
                                <span className="supplier-pitch__section-number inactive">3</span>
                                <h2 className="supplier-pitch__section-title">Upload Pitch Presentation</h2>
                            </div>
                            <div className="supplier-pitch__status-bar">
                                <div className="supplier-pitch__status-info">
                                    <span className="supplier-pitch__status-icon">
                                        <FileSkipped />
                                    </span>
                                    <span className="supplier-pitch__status-text">File Uploading Skipped</span>
                                </div>
                                <button type="button" className="supplier-pitch__status-action">
                                    <img src={editProfileInfo} alt="" onClick={() => setActiveStep(3)} />
                                </button>
                            </div>
                        </section>
                    );
                }
                return (
                    <section key="file" className="supplier-pitch__section">
                        <div className="supplier-pitch__section-header">
                            <span className="supplier-pitch__section-number inactive">3</span>
                            <h2 className="supplier-pitch__section-title">Upload Pitch Presentation</h2>
                        </div>
                        <div className="supplier-pitch__status-bar">
                            <div className="supplier-pitch__status-info">
                                <span className="supplier-pitch__status-icon">
                                    <img src={FileIcon} alt="" />
                                </span>
                                <span className="supplier-pitch__status-text">File Uploaded</span>
                            </div>
                            <button type="button" className="supplier-pitch__status-action">
                                <img src={editProfileInfo} alt="" onClick={() => setActiveStep(3)} />
                            </button>
                        </div>
                    </section>
                );
            }
            return null;
        })}
    </>
);
